<template>
  <div class="chart-renderer">
    <!-- Header du graphique -->
    <div v-if="config.display?.title || showHeader" class="chart-header mb-4">
      <h3 v-if="config.display?.title" class="text-lg font-semibold text-gray-900 mb-1">
        {{ config.display.title }}
      </h3>
      <div class="flex items-center justify-between text-sm text-gray-600">
        <span>{{ formatPeriod() }}</span>
        <div v-if="showStats" class="flex items-center gap-4">
          <span>{{ datasets.length }} série{{ datasets.length > 1 ? 's' : '' }}</span>
          <span>{{ totalDataPoints }} points</span>
        </div>
      </div>
    </div>

    <!-- État de chargement -->
    <div v-if="isLoading" class="loading-state">
      <div class="animate-pulse bg-gray-200 rounded-lg"
        :style="{ height: chartHeight + 'px' }">
        <div class="flex items-center justify-center h-full">
          <div class="text-gray-500">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500 inline-block" 
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Génération du graphique...
          </div>
        </div>
      </div>
    </div>

    <!-- État d'erreur -->
    <div v-else-if="error" class="error-state">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <svg class="w-8 h-8 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.262 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <p class="text-sm text-red-700 font-medium mb-1">Erreur de rendu</p>
        <p class="text-xs text-red-600">{{ error }}</p>
        <button @click="refresh" 
          class="mt-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 touch-manipulation">
          Réessayer
        </button>
      </div>
    </div>

    <!-- État vide -->
    <div v-else-if="isEmpty" class="empty-state">
      <div class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p class="text-sm text-gray-600 font-medium mb-1">{{ $t('charts.errors.no_data') }}</p>
        <p class="text-xs text-gray-500">{{ $t('data.unavailable') }}</p>
      </div>
    </div>

    <!-- Graphique principal ECharts -->
    <div v-else class="chart-container relative">
      <!-- Conteneur ECharts -->
      <div 
        ref="chartContainer" 
        :style="{ 
          width: '100%', 
          height: chartHeight + 'px',
          minHeight: chartHeight + 'px'
        }"
        class="echart-container">
      </div>
    </div>

    <!-- Informations détaillées (mode développeur) -->
    <div v-if="showDebugInfo" class="debug-info mt-4 p-3 bg-gray-100 rounded text-xs">
      <div class="font-medium mb-2">Informations de debug</div>
      <div class="grid grid-cols-2 gap-2">
        <div>Type: {{ config.chartType }}</div>
        <div>{{ $t('charts.mobile_info.mobile') }}: {{ isMobile ? $t('charts.mobile_info.yes') : $t('charts.mobile_info.no') }}</div>
        <div>{{ $t('charts.mobile_info.x_aggregation') }}: {{ config.xAxis.timeAggregation || $t('charts.mobile_info.none') }}</div>
        <div>Points total: {{ totalDataPoints }}</div>
        <div v-for="(ds, i) in config.dataSources" :key="i">
          Série {{ i + 1 }}: {{ ds.aggregation }} sur {{ ds.field }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick, markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { ECharts, EChartsOption, XAXisComponentOption, YAXisComponentOption } from 'echarts'
import { getFieldMetadata, type FlexibleChartConfig } from '../../types/customCharts'
import { MobileDataTransformer, TemporalAggregator, convertSalesApiToRawData, convertProductsApiToRawData, type RawDataPoint } from '../../utils/dataTransformer'
import type { SalesByIntervalResponse, ProductsReportResponse } from '../../responses'
import { CurrencyFormatOptions, formatAmount, formatCurrencyForCenter } from '@/utils/Languages'
import { useAppSettingsStore } from '@/stores/AppSettingsStore'

// Interface ECharts-compatible pour les datasets
interface EChartsDataset {
  label: string
  data: Array<{ 
    x: string | number | Date
    y: number
    label?: string
    metadata?: Record<string, any>
  }>
  color?: string
  yAxisIndex?: number
  seriesId?: string // ID de la série source
}

interface Props {
  config: FlexibleChartConfig
  rawData?: RawDataPoint[]
  // Nouvelles props pour données API par série
  seriesData?: Map<string, { 
    main: SalesByIntervalResponse | ProductsReportResponse | undefined,
    comparison?: SalesByIntervalResponse | ProductsReportResponse | undefined 
  }>
  // Props existantes
  centerId: number
  reportId?: string
  isMobile?: boolean
  compactMode?: boolean
  showHeader?: boolean
  showStats?: boolean
  showDebugInfo?: boolean
  height?: number
  width?: number
}

interface Emits {
  (e: 'dataPointClick', point: any): void
  (e: 'chartReady', chart: any): void
  (e: 'error', error: string): void
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  compactMode: false,
  showHeader: true,
  showStats: false,
  showDebugInfo: false,
  height: 300,
  width: undefined,
  centerId: undefined
})

const emit = defineEmits<Emits>()

// État du composant
const isLoading = ref(true)
const error = ref<string | null>(null)
const chartContainer = ref<HTMLDivElement | null>(null)
const chartInstance = ref<ECharts | null>(null)
const datasets = ref<EChartsDataset[]>([])
const resizeObserver = ref<ResizeObserver | null>(null)

const center = useAppSettingsStore().getCenter(props.centerId)

// Dimensions du graphique
const chartHeight = computed(() => {
  if (props.compactMode) return Math.min(props.height, 250)
  return props.isMobile ? Math.min(props.height, 350) : props.height
})

// État des données
const isEmpty = computed(() => {
  return datasets.value.length === 0 || 
         datasets.value.every(ds => ds.data.length === 0)
})

const totalDataPoints = computed(() => {
  return datasets.value.reduce((total, ds) => total + ds.data.length, 0)
})

// Transformateur de données
const dataTransformer = new MobileDataTransformer(props.isMobile, props.compactMode)

// Utilitaire pour s'assurer que la config a des périodes valides
const ensureValidConfig = (config: FlexibleChartConfig): FlexibleChartConfig => {
  const today = new Date()
  const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000))
  
  // Si les périodes ne sont pas définies, utiliser les 30 derniers jours
  if (!config.period.start || !config.period.end) {
    return {
      ...config,
      period: {
        start: thirtyDaysAgo.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      }
    }
  }
  
  return config
}

// Méthodes principales
const initializeChart = async () => {
  try {
    isLoading.value = true
    error.value = null

    // Vérifier que la configuration a des périodes valides, sinon utiliser des dates par défaut
    const configToUse = ensureValidConfig(props.config)
    
    // Déterminer la source de données à utiliser
    let rawDataBySeries: Map<string, RawDataPoint[]> = new Map()
    
    if (props.rawData) {
      // Données brutes fournies directement - assumer une seule série
      const firstSeries = configToUse.dataSources[0]?.id || 'default'
      rawDataBySeries.set(firstSeries, props.rawData)
    } else if (props.seriesData) {
      // Nouvelles données API par série
      for (const [seriesId, data] of props.seriesData) {
        const dataSource = configToUse.dataSources.find(ds => ds.id === seriesId)
        if (!dataSource || !data.main) continue

        let convertedData: RawDataPoint[] = []
        
        if (dataSource.source === 'sales') {
          convertedData = convertSalesApiToRawData(
            data.main as SalesByIntervalResponse,
            data.comparison as SalesByIntervalResponse
          )
        } else if (dataSource.source === 'products') {
          convertedData = convertProductsApiToRawData(
            data.main as ProductsReportResponse,
            data.comparison as ProductsReportResponse
          )
        }
        
        rawDataBySeries.set(seriesId, convertedData)
      }
    }
    
    // Si aucune donnée par série, utiliser les données combinées pour toutes les séries
    if (rawDataBySeries.size === 0 && configToUse.dataSources.length > 0) {
      // Fallback pour les anciens graphiques - utiliser des données vides
      configToUse.dataSources.forEach(ds => {
        rawDataBySeries.set(ds.id, [])
      })
    }
    // Transformer les données série par série
    const allDatasets: EChartsDataset[] = []
    
    for (const [seriesId, seriesRawData] of rawDataBySeries) {
      const dataSource = configToUse.dataSources.find(ds => ds.id === seriesId)
      if (!dataSource) continue
      
      // Créer une config temporaire pour cette série
      const seriesConfig = {
        ...configToUse,
        dataSources: [dataSource] // Une seule série pour la transformation
      }
      
      const transformedSeriesData = await dataTransformer.transform(seriesRawData, seriesConfig)
      
      // Ajouter les datasets transformés avec métadonnées de la série
      transformedSeriesData.forEach(dataset => {
        allDatasets.push({
          label: dataSource.name || dataset.label,
          data: dataset.data,
          color: dataset.backgroundColor as string || dataset.borderColor as string,
          yAxisIndex: dataSource.yAxisIndex || 0,
          seriesId: seriesId // Ajouter l'ID de série pour référence
        })
      })
    }

    datasets.value = allDatasets
    if (isEmpty.value) {
      isLoading.value = false
      return
    }

    // Arrêter le loading AVANT de créer le graphique pour que le conteneur soit dans le DOM
    isLoading.value = false
    
    // Attendre que le DOM se mette à jour
    await nextTick()
    
    // Créer le graphique
    await createChart()
    
  } catch (err) {
    console.error('Chart initialization error:', err)
    error.value = err instanceof Error ? err.message : t('charts.errors.unknown_error')
    isLoading.value = false
  }
}

const createChart = async () => {
  if (!chartContainer.value) {
    error.value = t('charts.errors.no_container')
    return
  }

  // Détruire l'ancien graphique si existant
  if (chartInstance.value) {
    chartInstance.value.dispose()
  }

  // Initialiser ECharts
  chartInstance.value = markRaw(echarts.init(chartContainer.value, undefined, {
    devicePixelRatio: window.devicePixelRatio || 2,
    renderer: props.isMobile ? 'canvas' : 'canvas', // Canvas plus performant sur mobile
    useDirtyRect: true, // Optimisation pour les updates
    width: 'auto',
    height: chartHeight.value
  }))

  // Configuration ECharts
  const option = buildEChartsOption()
  chartInstance.value.setOption(option, true)

  // Événements
  chartInstance.value.on('click', (params: any) => {
    emit('dataPointClick', {
      datasetIndex: params.seriesIndex,
      dataIndex: params.dataIndex,
      value: params.value,
      label: params.name,
      series: params.seriesName
    })
  })

  // Observer les redimensionnements (temporairement désactivé pour debug)
  // setupResizeObserver()

  emit('chartReady', chartInstance.value)
}

// Fonctions pour optimiser l'affichage des labels X
const getOptimalLabelRotation = (): number => {
  const dataCount = datasets.value[0]?.data.length || 0
  
  // Pas de rotation si peu de données
  if (dataCount <= 5) return 0
  
  // Sur mobile, rotation plus agressive
  if (props.isMobile) {
    if (dataCount > 15) return 45
    if (dataCount > 8) return 30
    return 0
  }
  
  // Sur desktop
  if (dataCount > 25) return 45
  if (dataCount > 15) return 30
  if (dataCount > 10) return 15
  
  return 0
}

const getOptimalLabelInterval = (): number | 'auto' => {
  const dataCount = datasets.value[0]?.data.length || 0
  
  // Laisser ECharts gérer automatiquement si peu de données
  if (dataCount <= 10) return 'auto'
  
  // Sur mobile, être plus sélectif
  if (props.isMobile) {
    if (dataCount > 30) return Math.floor(dataCount / 8) // ~8 labels max
    if (dataCount > 20) return Math.floor(dataCount / 6) // ~6 labels max
    if (dataCount > 15) return Math.floor(dataCount / 5) // ~5 labels max
    return 'auto'
  }
  
  // Sur desktop, plus de labels possibles
  if (dataCount > 50) return Math.floor(dataCount / 12) // ~12 labels max
  if (dataCount > 30) return Math.floor(dataCount / 10) // ~10 labels max
  if (dataCount > 20) return Math.floor(dataCount / 8)  // ~8 labels max
  
  return 'auto'
}

const buildEChartsOption = (): EChartsOption => {
  const series = datasets.value.map((dataset) => {
    // Pour éviter l'erreur coordSys, utilisons un format de données plus explicite
    const dataMapping = dataset.data.map((point) => ({
      value: point.y
    }))
    
    const seriesConfig: any = {
      name: dataset.label,
      data: dataMapping,
      color: dataset.color,
      yAxisIndex: dataset.yAxisIndex || 0
    }

    // Déterminer le type de graphique pour cette série
    let seriesChartType = props.config.chartType // Fallback
    
    if (dataset.seriesId) {
      const dataSource = props.config.dataSources.find(ds => ds.id === dataset.seriesId)
      seriesChartType = dataSource?.chartType || props.config.chartType
    }

    // Configuration spécifique selon le type de graphique de la série
    switch (seriesChartType) {
      case 'line':
        return {
          ...seriesConfig,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: props.isMobile ? 6 : 8,
          lineStyle: {
            width: props.isMobile ? 3 : 4,
            cap: 'round',
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowBlur: 10,
            shadowOffsetY: 2
          },
          itemStyle: {
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowBlur: 8,
            shadowOffsetY: 3
          },
          emphasis: {
            focus: 'series',
            blurScope: 'coordinateSystem',
            scale: 1.1,
            itemStyle: {
              shadowBlur: 15,
              shadowOffsetY: 5
            }
          }
        }
      
      case 'bar':
        return {
          ...seriesConfig,
          type: 'bar',
          barWidth: '60%',
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            shadowColor: 'rgba(0, 0, 0, 0.2)',
            shadowBlur: 8,
            shadowOffsetY: 3
          },
          emphasis: {
            focus: 'series',
            scale: 1.05,
            itemStyle: {
              shadowBlur: 15,
              shadowOffsetY: 6
            }
          }
        }
      
      case 'pie':
      case 'doughnut':
        return {
          ...seriesConfig,
          type: 'pie',
          radius: props.config.chartType === 'doughnut' ? ['35%', '75%'] : '75%',
          center: ['50%', '50%'],
          data: dataset.data.map((point, pointIndex) => ({
            name: point.label || point.x,
            value: point.y,
            itemStyle: {
              color: Array.isArray(dataset.color) ? dataset.color[pointIndex] : dataset.color,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowBlur: 10,
              shadowOffsetY: 5
            }
          })),
          emphasis: {
            scale: 1.05,
            itemStyle: {
              shadowBlur: 20,
              shadowOffsetY: 8,
              shadowColor: 'rgba(0, 0, 0, 0.4)'
            }
          },
          animationType: 'scale',
          animationEasing: 'elasticOut',
          label: {
            show: !props.compactMode,
            fontSize: props.isMobile ? 12 : 14,
          },
          labelLine: {
            show: !props.compactMode,
            smooth: true,
            length: props.isMobile ? 15 : 20,
            length2: props.isMobile ? 8 : 12
          }
        }
      
      case 'area':
        return {
          ...seriesConfig,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: {
            width: 3,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
            shadowBlur: 8,
            shadowOffsetY: 2
          },
          areaStyle: {
            opacity: 0.7,
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowBlur: 20,
            shadowOffsetY: 10
          },
          emphasis: {
            focus: 'series',
            blurScope: 'coordinateSystem',
            areaStyle: {
              opacity: 0.9
            }
          }
        }
      
      default:
        return {
          ...seriesConfig,
          type: 'line'
        }
    }
  })

  // Configuration globale ECharts
  const option: EChartsOption = {
    // Animation optimisée mobile avec effets modernes
    animation: true,
    animationDuration: props.isMobile ? 600 : 1200,
    animationEasing: 'elasticOut',
    animationDelay: (idx: number) => idx * 50, // Animation en cascade
    
    // Grid (zone de graphique)
    grid: {
      left: props.isMobile ? '0%' : '3%',
      right: props.config.yAxis2 ? (props.isMobile ? '0%' : '10%') : (props.isMobile ? '0%' : '4%'),
      bottom: props.isMobile ? '15%' : '3%',
      top: props.isMobile ? '10%' : '10%',
      containLabel: true
    },
    
    // Tooltip mobile-optimisé
    tooltip: {
      show: true,
      trigger: ['pie', 'doughnut'].includes(props.config.chartType) ? 'item' : 'axis',
      triggerOn: 'mousemove',
      enterable: false,
      hideDelay: 200,
      showDelay: 100,
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#666',
          width: 1,
          type: 'dashed'
        }
      },
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      borderWidth: 0,
      borderRadius: 6,
      padding: [8, 12],
      textStyle: {
        color: '#fff',
        fontSize: props.isMobile ? 11 : 12,
        lineHeight: 1.4
      },
      formatter: function(params: any) {
        if (!params) return t('charts.errors.no_data')
        
        if (Array.isArray(params)) {
          if (params.length === 0) return t('charts.errors.no_data')
          // Contenu simplifié et neutre
          let result = params.map((param: any) => {
            const value = formatValue(param.value)
            return `<div style="margin: 2px 0; display: flex; align-items: center;">
              <span style="display: inline-block; margin-right: 8px; width: 10px; height: 10px; background-color: ${param.color || '#ccc'}"></span>
              <span><strong>${value}</strong></span>
            </div>`
          }).join('')
          
          return result
        } else {
          // Single item (pour les graphiques pie/doughnut)
          const value = formatValue(params.value)
          const percentage = params.percent ? ` (${params.percent}%)` : ''
          
          return `<div style="display: flex; align-items: center;">
            <span style="display: inline-block; margin-right: 8px; width: 10px; height: 10px; background-color: ${params.color || '#ccc'}"></span>
            <span><strong>${value}</strong>${percentage}</span>
          </div>`
        }
      }
    },
    
    // Séries
    series,
    
    // Responsive
    media: [
      {
        query: {
          maxWidth: 640
        },
        option: {
          grid: {
            left: '5%',
            right: '5%'
          }
        }
      }
    ]
  }

  // Détermine si le graphique utilise des coordonnées cartésiennes
  const usesCartesianCoordinates = () => {
    // Si coordinateSystem est défini, l'utiliser
    if (props.config.coordinateSystem) {
      return props.config.coordinateSystem === 'cartesian'
    }
    
    // Sinon, vérifier si au moins une série utilise un type cartésien
    const hasCartesianSeries = props.config.dataSources.some(ds => {
      const chartType = ds.chartType || props.config.chartType
      return ['line', 'bar', 'area'].includes(chartType)
    })
    
    // Fallback: utiliser l'ancien système
    return ['line', 'bar', 'area'].includes(props.config.chartType) || hasCartesianSeries
  }

  // Axes pour les graphiques cartésiens
  if (usesCartesianCoordinates()) {
    // Validation des données avant création des axes
    if (!datasets.value.length || !datasets.value[0]?.data?.length) {
      console.error('No valid data for axis creation')
      return option
    }
    
    // Utiliser l'axe catégoriel avec les labels des données
    const categories = datasets.value[0]?.data.map(point => {
      // Essayer d'abord groupKey, puis label, puis x comme fallback
      return point.x || point.label || point.metadata?.groupKey
    }) || []
    
    // Validation des catégories
    if (categories.length === 0) {
      console.error('No categories generated for X axis')
      return option
    }
    
    const categoryAxisConfig: XAXisComponentOption = {
      type: 'category',
      data: categories,
      name: props.config.xAxis.label,
      nameLocation: 'middle',
      nameGap: props.isMobile ? 25 : 30,
      nameTextStyle: {
        fontSize: props.isMobile ? 10 : 12
      },
      axisLabel: {
        fontSize: props.isMobile ? 9 : 11,
        rotate: getOptimalLabelRotation(),
        interval: getOptimalLabelInterval(),
        formatter: (value: string) => {
          return TemporalAggregator.getDisplayLabel(value, props.config.xAxis.timeAggregation!)
        }
      },
      axisTick: {
        show: true
      },
      boundaryGap: false, // Important pour éviter l'erreur coordSys
      splitLine: {
        show: false
      }
    }
    option.xAxis = categoryAxisConfig

    // Configuration de l'axe Y 
    const yAxisConfig: YAXisComponentOption = {
      type: 'value',
      // name: props.config.yAxis.label,
      nameLocation: 'middle',
      nameGap: props.isMobile ? 35 : 50,
      nameTextStyle: {
        fontSize: props.isMobile ? 10 : 12
      },
      axisLabel: {
        fontSize: props.isMobile ? 9 : 11,
        formatter: (value: number) => formatValue(value)
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#e5e7eb',
          opacity: 0.7
        }
      }
    }
    option.yAxis = yAxisConfig
  }

  // Déterminer si le graphique contient des types polaires
  const hasPolarSeries = () => {
    if (props.config.coordinateSystem === 'polar') return true
    
    return props.config.dataSources.some(ds => {
      const chartType = ds.chartType || props.config.chartType
      return ['pie', 'doughnut'].includes(chartType)
    })
  }

  // Légende intégrée ECharts (si pas de légende personnalisée)
  if (props.config.display?.showLegend && datasets.value.length > 1 && !hasPolarSeries()) {
    option.legend = {
      show: false // On gère notre propre légende
    }
  } else if (hasPolarSeries()) {
    option.legend = {
      show: false // Désactiver la légende pour les graphiques polaires
    }
  }

  // DataZoom pour les graphiques avec beaucoup de données sur mobile
  const hasZoomableChartTypes = usesCartesianCoordinates() && props.config.dataSources.some(ds => {
    const chartType = ds.chartType || props.config.chartType
    return ['line', 'bar'].includes(chartType)
  })
  
  if (props.isMobile && totalDataPoints.value > 50 && hasZoomableChartTypes) {
    option.dataZoom = [{
      type: 'inside',
      xAxisIndex: 0,
      start: 70,
      end: 100,
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      moveOnMouseWheel: true
    }]
  }

  // console.log(JSON.stringify(option, null, 2))
  return option
}

// Observer pour redimensionnement
// const setupResizeObserver = () => {
//   if (!chartContainer.value || !chartInstance.value) return

//   resizeObserver.value = new ResizeObserver((entries) => {
//     for (const entry of entries) {
//       if (chartInstance.value) {
//         chartInstance.value.resize({
//           width: entry.contentRect.width,
//           height: entry.contentRect.height
//         })
//       }
//     }
//   })

//   resizeObserver.value.observe(chartContainer.value)
// }

// Gestion du redimensionnement de la fenêtre
const handleWindowResize = () => {
  if (chartInstance.value) {
    chartInstance.value.resize()
  }
}

// Utilitaires
const formatValue = (value?: number): string => {
  if (value === undefined) return ''
  let source = props.config.dataSources[0].source;
  let field = props.config.dataSources[0].field;
  let metadata = getFieldMetadata(source, field);
  
  // Format selon le type de données
  if (metadata?.type === 'currency') {
    let formatOptions: CurrencyFormatOptions = {
      center: center,
      compact: true,
    }

    return formatAmount(value, formatOptions)
  }
  
  return formatCurrencyForCenter(value, center)
}

const formatPeriod = (): string => {
  const start = new Date(props.config.period.start).toLocaleDateString('fr-FR')
  const end = new Date(props.config.period.end).toLocaleDateString('fr-FR')
  return `${start} - ${end}`
}

const refresh = () => {
  initializeChart()
}

// Variable pour suivre l'état du montage
const isMounted = ref(false)

// Watchers
watch(() => props.config, () => {
  if (isMounted.value) {
    initializeChart()
  }
}, { deep: true })

watch(() => props.rawData, () => {
  if (props.rawData && isMounted.value) {
    initializeChart()
  }
}, { deep: true })

// Lifecycle
onMounted(async () => {
  // Écouter les redimensionnements de la fenêtre
  window.addEventListener('resize', handleWindowResize)
  
  // Marquer comme monté
  isMounted.value = true
  
  // Attendre que le DOM soit complètement prêt
  await nextTick()
  
  // Initialiser le graphique
  initializeChart()
})

onUnmounted(() => {
  // Nettoyer l'observer de redimensionnement
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
    resizeObserver.value = null
  }
  
  // Nettoyer l'instance ECharts
  if (chartInstance.value) {
    chartInstance.value.dispose()
    chartInstance.value = null
  }
  
  // Nettoyer les event listeners
  window.removeEventListener('resize', handleWindowResize)
})

// Exposition pour usage externe
defineExpose({
  refresh,
  getChart: () => chartInstance.value,
  exportImage: () => {
    if (chartInstance.value) {
      return chartInstance.value.getDataURL({
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
    }
    return null
  }
})
</script>

<style scoped>
@reference "../../style.css";

/* Optimisations tactiles */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.touch-none {
  touch-action: none;
}

/* Animation de chargement */
.loading-state .animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .7;
  }
}

/* Animation pour le spinner */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Tooltip mobile */
.mobile-tooltip {
  transform: translateX(-50%);
  max-width: 200px;
  font-size: 0.75rem;
  line-height: 1.2;
  z-index: 1000;
}

/* Légende mobile-optimisée */
.chart-legend button {
  transition: all 0.2s ease-in-out;
}

.chart-legend button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* États hover pour desktop seulement */
@media (hover: hover) {
  .hover\:bg-gray-200:hover {
    background-color: rgb(229 231 235);
  }
  
  .hover\:bg-red-200:hover {
    background-color: rgb(254 202 202);
  }
}

/* Responsive canvas */
.chart-container canvas {
  width: 100% !important;
  height: auto !important;
}

/* Debug info */
.debug-info {
  font-family: 'Courier New', monospace;
  border-left: 3px solid #3B82F6;
}

/* Améliorations pour petits écrans */
@media (max-width: 640px) {
  .chart-header .text-lg {
    font-size: 1rem;
  }
  
  .chart-legend {
    max-height: 120px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>